/**
 * The builder's working tree, and how it becomes an AST.
 *
 * Deliberately NOT the AST itself. The editor needs things the saved definition
 * must not carry: a stable id per node so React can key rows through reordering,
 * and half-finished conditions that are not yet valid. Keeping them separate
 * means the AST stays exactly what §7 says it is — a serialisable query
 * definition — instead of accumulating editor state that would then be stored
 * forever inside every saved universe.
 */

import type { Comparator, FieldKind } from '@blocwork/universe/fields'

/**
 * Types come from the shared universe package rather than being redeclared
 * here, which is the point of its browser-safe subpath exports: adding a
 * comparator to the registry becomes a type error in this file instead of a
 * silent divergence that only shows up as a rejected AST at runtime. These are
 * type-only imports, so nothing is added to the bundle.
 */
export type ComparatorName = Comparator

/** One canonical outcome, as /api/dispositions returns it. */
export interface DispositionMeta {
  code: string
  label: string
  is_contact: boolean
  support_level: number | null
  is_final: boolean
}

export interface FieldMeta {
  name: string
  label: string
  kind: FieldKind
  ops: ComparatorName[]
}

export type BuilderNode = GroupNode | FieldNode | ParticipationNode | ContactNode | SurveyNode | DoorVisitNode

/** A filterable survey question, as /api/surveys/filterable returns it. */
export interface SurveyQuestionMeta {
  question_code: string
  prompt: string
  kind: 'single' | 'multi' | 'scale' | 'number' | 'text'
  answers: { code: string; label: string }[]
}

export interface GroupNode {
  id: string
  kind: 'group'
  op: 'and' | 'or'
  /** Wraps the group in a NOT when compiled. */
  negated: boolean
  children: BuilderNode[]
}

export interface FieldNode {
  id: string
  kind: 'field'
  field: string
  cmp: ComparatorName
  /** Array for in/not_in/between, scalar otherwise, empty while being filled in. */
  value: string | number | boolean | (string | number)[] | null
}

export interface ParticipationNode {
  id: string
  kind: 'participation'
  electionType: string
  recentCount: number
  mode: 'at_least' | 'at_most' | 'exactly' | 'none' | 'all'
  count: number
}

/**
 * A predicate over what the campaign heard back.
 *
 * The editor keeps `match` flat — a `by` plus the fields any variant might
 * need — rather than mirroring the AST's discriminated union. Switching from
 * "said yes" to "marked as" and back should not throw away what was typed,
 * and a union in the editor state means it does.
 */
export interface ContactNode {
  id: string
  kind: 'contact'
  scope: 'latest' | 'any'
  /** Empty means any method. */
  methods: string[]
  by: 'any' | 'reached' | 'disposition' | 'support'
  codes: string[]
  min: number
  max: number
  /** Whether each bound is in play, since 0 is a meaningful support level. */
  useMin: boolean
  useMax: boolean
  since: string
  before: string
}

/**
 * A condition on what a voter said on a survey. Flat like ContactNode — a `by`
 * plus every field a variant might need — so switching between "answered",
 * "chose" and "value in range" keeps what was typed. `question` is the stable
 * question code; the builder resolves it to a prompt and answer options from
 * the campaign's filterable questions.
 */
export interface SurveyNode {
  id: string
  kind: 'survey'
  question: string
  scope: 'latest' | 'any'
  by: 'answered' | 'answer' | 'value'
  /** Chosen answer codes, for by:answer. */
  codes: string[]
  min: number
  max: number
  useMin: boolean
  useMax: boolean
}

/**
 * The door was visited and nobody was spoken to (0090, 0093). Matches every
 * voter behind the door. Paired with "nobody reached" it is the knock-again
 * list.
 */
export interface DoorVisitNode {
  id: string
  kind: 'door_visit'
  /** Empty means either outcome. */
  outcomes: ('not_home' | 'inaccessible')[]
  minTimes: number
  since: string
  before: string
}

export function newDoorVisitNode(): DoorVisitNode {
  return { id: newId(), kind: 'door_visit', outcomes: ['not_home'], minTimes: 1, since: '', before: '' }
}

/**
 * Knock again: doors that were not home, where nobody has been reached since —
 * by a later knock or by phone. A group so it reads, and edits, as one idea.
 */
export function newKnockAgainGroup(): GroupNode {
  const notReached: GroupNode = {
    ...emptyGroup('and'),
    negated: true,
    children: [{ ...newContactNode(), by: 'reached', scope: 'any', useMin: false }],
  }
  return { ...emptyGroup('and'), children: [newDoorVisitNode(), notReached] }
}

export const CONTACT_METHOD_OPTIONS = [
  { value: 'walk', label: 'Canvass' },
  { value: 'phone', label: 'Phone' },
  { value: 'text', label: 'Text' },
  { value: 'mail', label: 'Mail' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
]

const newId = () => crypto.randomUUID()

export function emptyGroup(op: 'and' | 'or' = 'and'): GroupNode {
  return { id: newId(), kind: 'group', op, negated: false, children: [] }
}

export function newFieldNode(field: FieldMeta): FieldNode {
  // Default to the operator that reads most naturally for the type: a set
  // membership for enums, a comparison for everything else.
  const cmp = field.kind === 'enum' && field.ops.includes('in') ? 'in' : (field.ops[0] ?? 'eq')
  return {
    id: newId(),
    kind: 'field',
    field: field.name,
    cmp,
    value: cmp === 'in' || cmp === 'not_in' || cmp === 'between' ? [] : null,
  }
}

export function newParticipationNode(): ParticipationNode {
  return {
    id: newId(),
    kind: 'participation',
    electionType: 'GEN',
    recentCount: 4,
    mode: 'at_least',
    count: 2,
  }
}

export function newContactNode(): ContactNode {
  // Defaults to the question campaigns ask most: who said yes. min 1 is the
  // supportive half of the -2…+2 scale, whatever tokens the campaign printed.
  return {
    id: newId(),
    kind: 'contact',
    scope: 'latest',
    methods: [],
    by: 'support',
    codes: [],
    min: 1,
    max: 2,
    useMin: true,
    useMax: false,
    since: '',
    before: '',
  }
}

export function newSurveyNode(question?: SurveyQuestionMeta): SurveyNode {
  return {
    id: newId(),
    kind: 'survey',
    question: question?.question_code ?? '',
    scope: 'latest',
    by: 'answered',
    codes: [],
    min: 1,
    max: 2,
    useMin: true,
    useMax: false,
  }
}

/** Operators that take no value at all. */
export const VALUELESS = new Set(['is_null', 'is_not_null'])
/** Operators that take a list. */
export const MULTI = new Set(['in', 'not_in'])

/**
 * True when a node is complete enough to compile. Incomplete nodes are skipped
 * rather than sent, so a count keeps working while a condition is half-typed —
 * the alternative is a red error every time someone opens a new row.
 */
export function isComplete(node: BuilderNode): boolean {
  if (node.kind === 'group') return node.children.some(isComplete)
  if (node.kind === 'participation') return node.recentCount > 0
  if (node.kind === 'door_visit') return node.minTimes >= 1
  if (node.kind === 'contact') {
    if (node.by === 'disposition') return node.codes.length > 0
    // A support predicate with neither bound matches everyone with any support
    // level recorded, which is not what an empty form means — and the compiler
    // rejects it outright. Treated as incomplete so the count keeps working
    // while someone is still choosing.
    if (node.by === 'support') return node.useMin || node.useMax
    return true
  }
  if (node.kind === 'survey') {
    if (!node.question) return false
    if (node.by === 'answer') return node.codes.length > 0
    if (node.by === 'value') return node.useMin || node.useMax
    return true
  }
  if (VALUELESS.has(node.cmp)) return true
  if (Array.isArray(node.value)) {
    return node.cmp === 'between' ? node.value.length === 2 && node.value.every((v) => v !== '') : node.value.length > 0
  }
  return node.value !== null && node.value !== ''
}

/** The builder tree as a §7 AST, or null when nothing is complete yet. */
export function toAst(node: BuilderNode): unknown | null {
  if (node.kind === 'group') {
    const children = node.children.filter(isComplete).map(toAst).filter(Boolean)
    if (children.length === 0) return null
    // A single child needs no wrapper; emitting {and:[x]} would make saved
    // definitions noisier than the thing the user actually built.
    const inner = children.length === 1 ? children[0] : { op: node.op, children }
    return node.negated ? { op: 'not', child: inner } : inner
  }

  if (node.kind === 'participation') {
    return {
      op: 'participation',
      elections: { by: 'recent', count: node.recentCount, election_type: node.electionType },
      mode: node.mode,
      ...(node.mode === 'none' || node.mode === 'all' ? {} : { count: node.count }),
    }
  }

  if (node.kind === 'contact') {
    const match =
      node.by === 'disposition'
        ? { by: 'disposition', codes: node.codes }
        : node.by === 'support'
          ? {
              by: 'support',
              ...(node.useMin ? { min: node.min } : {}),
              ...(node.useMax ? { max: node.max } : {}),
            }
          : { by: node.by }

    // Only what was actually set. The predicate is strict, so an empty string
    // for `since` would be a parse error rather than "no lower bound".
    return {
      op: 'contact',
      scope: node.scope,
      ...(node.methods.length > 0 ? { methods: node.methods } : {}),
      ...(node.since ? { since: node.since } : {}),
      ...(node.before ? { before: node.before } : {}),
      match,
    }
  }

  if (node.kind === 'door_visit') {
    // Only what was set — the predicate is strict (0093).
    return {
      op: 'door_visit',
      ...(node.outcomes.length > 0 ? { outcomes: node.outcomes } : {}),
      ...(node.minTimes > 1 ? { min_times: node.minTimes } : {}),
      ...(node.since ? { since: node.since } : {}),
      ...(node.before ? { before: node.before } : {}),
    }
  }

  if (node.kind === 'survey') {
    const match =
      node.by === 'answer'
        ? { by: 'answer', codes: node.codes }
        : node.by === 'value'
          ? {
              by: 'value',
              ...(node.useMin ? { min: node.min } : {}),
              ...(node.useMax ? { max: node.max } : {}),
            }
          : { by: 'answered' }
    return { op: 'survey', question: node.question, scope: node.scope, match }
  }

  if (VALUELESS.has(node.cmp)) return { op: 'field', field: node.field, cmp: node.cmp }
  return { op: 'field', field: node.field, cmp: node.cmp, value: node.value }
}

/**
 * The inverse of toAst: a saved definition back into an editable tree.
 *
 * Not symmetric with toAst, and it cannot be. toAst drops the group wrapper
 * around a single condition and folds a NOT into its group, so the AST is a
 * normalised form with less structure than the tree that produced it. Loading
 * therefore reconstructs a tree that is *equivalent*, not identical — a saved
 * universe may come back with its conditions arranged slightly differently
 * from how they were typed. Re-saving it produces the same AST, which is the
 * property that actually matters.
 *
 * Unknown shapes are skipped rather than thrown on: an AST written by a newer
 * version of the app should degrade to "most of it loaded", not a blank screen.
 */
export function fromAst(ast: unknown): GroupNode {
  const node = convert(ast)
  if (node?.kind === 'group') return node
  return { ...emptyGroup('and'), children: node ? [node] : [] }
}

function convert(ast: unknown): BuilderNode | null {
  if (!ast || typeof ast !== 'object') return null
  const value = ast as Record<string, unknown>

  if (value['op'] === 'and' || value['op'] === 'or') {
    const children = Array.isArray(value['children'])
      ? (value['children'] as unknown[]).map(convert).filter((c): c is BuilderNode => c !== null)
      : []
    return { id: newId(), kind: 'group', op: value['op'], negated: false, children }
  }

  if (value['op'] === 'not') {
    const inner = convert(value['child'])
    if (!inner) return null
    // A NOT around a group becomes that group's `negated` flag; a NOT around a
    // single condition needs a group to carry the flag, since only groups have
    // one.
    if (inner.kind === 'group') return { ...inner, negated: !inner.negated }
    return { id: newId(), kind: 'group', op: 'and', negated: true, children: [inner] }
  }

  if (value['op'] === 'field' && typeof value['field'] === 'string') {
    return {
      id: newId(),
      kind: 'field',
      field: value['field'],
      cmp: (value['cmp'] as ComparatorName) ?? 'eq',
      value: (value['value'] as FieldNode['value']) ?? null,
    }
  }

  if (value['op'] === 'contact') {
    const match = (value['match'] ?? {}) as Record<string, unknown>
    const by = (match['by'] as ContactNode['by']) ?? 'any'
    return {
      ...newContactNode(),
      id: newId(),
      scope: (value['scope'] as ContactNode['scope']) ?? 'latest',
      methods: Array.isArray(value['methods']) ? (value['methods'] as string[]) : [],
      since: (value['since'] as string) ?? '',
      before: (value['before'] as string) ?? '',
      by,
      codes: Array.isArray(match['codes']) ? (match['codes'] as string[]) : [],
      // A bound that was not saved is not in play. Reading a missing min as 0
      // would turn "supporters" into "anyone not opposed" on a round trip.
      useMin: typeof match['min'] === 'number',
      useMax: typeof match['max'] === 'number',
      min: typeof match['min'] === 'number' ? match['min'] : 1,
      max: typeof match['max'] === 'number' ? match['max'] : 2,
    }
  }

  if (value['op'] === 'survey') {
    const match = (value['match'] ?? {}) as Record<string, unknown>
    return {
      ...newSurveyNode(),
      id: newId(),
      question: (value['question'] as string) ?? '',
      scope: (value['scope'] as SurveyNode['scope']) ?? 'latest',
      by: (match['by'] as SurveyNode['by']) ?? 'answered',
      codes: Array.isArray(match['codes']) ? (match['codes'] as string[]) : [],
      // A missing bound was not in play, same as the contact round trip.
      useMin: typeof match['min'] === 'number',
      useMax: typeof match['max'] === 'number',
      min: typeof match['min'] === 'number' ? match['min'] : 1,
      max: typeof match['max'] === 'number' ? match['max'] : 2,
    }
  }

  if (value['op'] === 'door_visit') {
    return {
      ...newDoorVisitNode(),
      outcomes: Array.isArray(value['outcomes'])
        ? (value['outcomes'] as DoorVisitNode['outcomes'])
        : [],
      minTimes: typeof value['min_times'] === 'number' ? value['min_times'] : 1,
      since: (value['since'] as string) ?? '',
      before: (value['before'] as string) ?? '',
    }
  }

  if (value['op'] === 'participation') {
    const elections = (value['elections'] ?? {}) as Record<string, unknown>
    // Only the 'recent' selector has an editor. A saved universe pinned to
    // specific election ids is still valid and still counts correctly; it just
    // cannot be edited here yet, so it is dropped rather than silently
    // rewritten into something that means something else.
    if (elections['by'] !== 'recent') return null
    return {
      id: newId(),
      kind: 'participation',
      electionType: (elections['election_type'] as string) ?? 'GEN',
      recentCount: (elections['count'] as number) ?? 4,
      mode: (value['mode'] as ParticipationNode['mode']) ?? 'at_least',
      count: (value['count'] as number) ?? 1,
    }
  }

  return null
}

/** Immutable tree edit by id. Returning null removes the node. */
export function updateNode(
  root: BuilderNode,
  id: string,
  change: (node: BuilderNode) => BuilderNode | null,
): BuilderNode | null {
  if (root.id === id) return change(root)
  if (root.kind !== 'group') return root
  return {
    ...root,
    children: root.children
      .map((child) => updateNode(child, id, change))
      .filter((child): child is BuilderNode => child !== null),
  }
}

export function countConditions(node: BuilderNode): number {
  return node.kind === 'group' ? node.children.reduce((n, c) => n + countConditions(c), 0) : 1
}
